import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Pin, Plus, X } from 'lucide-react'
import { GAME_WINDOW_OPTIONS, gameWindowLabel, mixDefaults, mixFormError, mixFormWarning, mixFromForm, mixTargetsTotal, sameMix, sportLabel } from './prepackLineupModel'
import { useLineupPanel } from './useLineupPanel'

const LIMIT_FIELDS = [
  ['maxPerPlayer', 'Max per player', 'players'],
  ['maxPerTeam', 'Max per team', 'teams'],
  ['maxPerMarket', 'Max per market', 'markets'],
]

// A sport's dot in the projected lineup: one hue per sport, stable across renders.
function sportHue(sport = '') {
  let hash = 0
  for (const char of sport) hash = (hash * 31 + char.charCodeAt(0)) % 360
  return `hsl(${hash} 70% 55%)`
}

const inputClass = 'w-16 rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-2 py-1 text-xs tabular-nums text-[var(--wf-color-text)] outline-none focus:border-[var(--prepacks-accent-text)] disabled:opacity-60'
const selectClass = 'min-w-0 rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-2 py-1 text-xs text-[var(--prepacks-text)] disabled:opacity-60'

/**
 * Manage mix (#990): Balanced or Custom settings for the selected surface,
 * with the lineup they would give beside them. Changing a setting updates the
 * projection; Save applies it to the live surface, Cancel discards it.
 */
export function SurfaceMixPanel({ surface, liveView, pageView = Boolean(liveView?.context_key), canConfigure, saving, onPreview, onSave, onCancel }) {
  // Beside the cards only where both fit: the surfaces rail, 640px of
  // panel and room for a column of cards.
  const { ref } = useLineupPanel({ inlineQuery: '(min-width: 1280px)', onClose: onCancel })
  // latest is the surface's mix as saved now; base is the one the draft
  // started from. A card action beside the panel moves the surface's
  // version but not its mix, so the draft stays; a mix saved elsewhere
  // meanwhile is shown, and the draft must be started again from it.
  const latest = useMemo(() => mixDefaults(surface), [surface])
  const [base, setBase] = useState(latest)
  const [form, setForm] = useState(latest)
  const [debounced, setDebounced] = useState(latest)
  const changedElsewhere = !sameMix(base, latest)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(form), 250)
    return () => clearTimeout(timer)
  }, [form])
  const dirty = !sameMix(form, base)
  const displayCount = Math.max(1, Math.min(50, Number(surface.layout_config?.max_display_count) || 10))
  const error = mixFormError(form)
  const warning = pageView ? null : mixFormWarning(form, displayCount)
  const previewDirty = !sameMix(debounced, latest)
  const previewError = mixFormError(debounced)
  // A card action beside the panel (a pin, an exclusion, a replace)
  // changes the surface or the stored lineup, and so the projection too.
  const previewQuery = useQuery({
    queryKey: ['prepacks', 'surfaces', surface.slug, 'mix-preview', liveView?.regulation, liveView?.context_key, surface.updated_at, liveView?.version, mixFromForm(debounced)],
    queryFn: ({ signal }) => onPreview(mixFromForm(debounced), { signal }),
    enabled: previewDirty && !previewError,
    staleTime: 0,
    placeholderData: (previous) => previous,
  })
  // Unchanged settings project the live lineup itself.
  const projection = previewDirty ? previewQuery.data : liveView
  const set = (patch) => setForm((current) => ({ ...current, ...patch }))
  const sportsToAdd = (liveView?.sports || []).filter((sport) => !(sport in form.sportTargets))
  const orgLimits = liveView?.org_limits || null
  const custom = form.mode === 'custom'
  const limitPlaceholder = (kind) => (kind && orgLimits ? String(orgLimits[kind] ?? '') : '')
  const windowHours = Number(form.gameWindowHours) || 0
  // A saved window the list does not offer (set through the API) is listed
  // so it is kept, never rounded away.
  const windowOptions = GAME_WINDOW_OPTIONS.some((option) => option.value === windowHours) ? GAME_WINDOW_OPTIONS : [...GAME_WINDOW_OPTIONS, { value: windowHours, label: gameWindowLabel(windowHours) }]

  return (
    <aside ref={ref} tabIndex={-1} aria-label="Manage mix" className="fixed inset-0 z-40 flex flex-col bg-[var(--prepacks-surface)] text-[var(--wf-color-text)] xl:static xl:z-auto xl:w-[640px] xl:shrink-0 xl:border-l xl:border-[var(--prepacks-border)]">
      <header className="flex flex-wrap items-center gap-2 border-b border-[var(--prepacks-border)] px-4 py-3">
        <h2 className="min-w-0 truncate text-sm font-semibold">Manage mix — {surface.name}</h2>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={onCancel} className="rounded border border-[var(--prepacks-border)] px-3 py-1 text-xs text-[var(--prepacks-text)] hover:bg-[var(--prepacks-raised)]">Cancel</button>
          {canConfigure ? <button type="button" disabled={!dirty || Boolean(error) || saving || changedElsewhere} onClick={() => onSave(mixFromForm(form))} className="rounded bg-[var(--prepacks-accent)] px-3 py-1 text-xs font-semibold text-[var(--wf-color-on-action)] hover:bg-[var(--prepacks-accent-hover)] disabled:opacity-50">{saving ? 'Saving…' : 'Save changes'}</button> : null}
          <button type="button" aria-label="Close manage mix" onClick={onCancel} className="rounded p-1 text-[var(--prepacks-muted)] hover:text-[var(--wf-color-text)]"><X className="h-4 w-4" /></button>
        </div>
      </header>
      <div role="group" aria-label="Mix mode" className="mx-4 mt-3 grid grid-cols-2 rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] p-0.5 text-xs">
        {[['balanced', 'Balanced'], ['custom', 'Custom']].map(([mode, label]) => (
          <button key={mode} type="button" aria-pressed={form.mode === mode} disabled={!canConfigure} onClick={() => set({ mode })} className={`rounded px-3 py-1.5 ${form.mode === mode ? 'bg-[var(--wf-color-action-soft)] font-semibold text-[var(--prepacks-accent-text)]' : 'text-[var(--prepacks-muted)] hover:text-[var(--wf-color-text)]'}`}>{label}</button>
        ))}
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto p-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <form className="flex min-w-0 flex-col gap-4 text-xs" onSubmit={(event) => event.preventDefault()}>
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 font-semibold">Repeats</legend>
            {LIMIT_FIELDS.map(([field, label, kind]) => (
              <label key={field} className="flex items-center justify-between gap-2 text-[var(--prepacks-muted)]">{label}
                <input type="number" min="0" max="20" aria-label={label} placeholder={limitPlaceholder(kind)} disabled={!canConfigure} value={form[field]} onChange={(event) => set({ [field]: event.target.value })} className={inputClass} />
              </label>
            ))}
            <p className="text-[var(--prepacks-muted)]">Blank uses the default; 0 means no limit.</p>
            <label className="flex items-start gap-2"><input type="checkbox" disabled={!canConfigure} checked={form.allowRepeats} onChange={(event) => set({ allowRepeats: event.target.checked })} className="mt-0.5" />Allow repeats to fill available slots</label>
          </fieldset>
          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1 font-semibold">Game window</legend>
            <label className="flex items-center justify-between gap-2 text-[var(--prepacks-muted)]">Games starting within
              <select aria-label="Games starting within" disabled={!canConfigure} value={String(windowHours)} onChange={(event) => set({ gameWindowHours: event.target.value })} className={selectClass}>
                {windowOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            <p className="text-[var(--prepacks-muted)]">Published packs wait until their first game is within the window, then show as positions open. Schedules keep their own “Games starting within”.</p>
          </fieldset>
          {!custom ? (
            <p className="text-[var(--prepacks-muted)]">Balanced prioritises variety and keeps pinned positions.</p>
          ) : (
            <>
              {!pageView ? (
                <fieldset className="flex flex-col gap-1.5">
                  <legend className="mb-1 font-semibold">Sport allocation</legend>
                  {Object.entries(form.sportTargets).map(([sport, target]) => (
                    <div key={sport} className="flex items-center gap-2">
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: sportHue(sport) }} aria-hidden="true" />
                      <span className="min-w-0 flex-1 truncate">{sportLabel(sport)}</span>
                      <input type="number" min="0" max="50" aria-label={`${sportLabel(sport)} target`} disabled={!canConfigure} value={target} onChange={(event) => set({ sportTargets: { ...form.sportTargets, [sport]: event.target.value } })} className={inputClass} />
                      <button type="button" aria-label={`Remove ${sportLabel(sport)}`} disabled={!canConfigure} onClick={() => { const next = { ...form.sportTargets }; delete next[sport]; set({ sportTargets: next }) }} className="rounded p-1 text-[var(--prepacks-muted)] hover:text-[var(--wf-color-text)]"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                  {sportsToAdd.length > 0 && canConfigure ? (
                    <label className="flex items-center gap-1.5 text-[var(--prepacks-accent-text)]"><Plus className="h-3 w-3" aria-hidden="true" />
                      <select aria-label="Add sport" value="" onChange={(event) => event.target.value && set({ sportTargets: { ...form.sportTargets, [event.target.value]: 1 } })} className="min-w-0 flex-1 rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-2 py-1 text-xs text-[var(--prepacks-text)]">
                        <option value="">Add sport</option>
                        {sportsToAdd.map((sport) => <option key={sport} value={sport}>{sportLabel(sport)}</option>)}
                      </select>
                    </label>
                  ) : null}
                  <p className="text-[var(--prepacks-muted)]">{mixTargetsTotal(form) > 0 ? `${mixTargetsTotal(form)} of ${displayCount} slots allocated.` : 'Fill remaining slots with eligible packs.'}</p>
                </fieldset>
              ) : null}
              <label className="flex items-center justify-between gap-2 text-[var(--prepacks-muted)]">Max per schedule
                <input type="number" min="0" max="50" aria-label="Max per schedule" disabled={!canConfigure} value={form.maxPerRecipe} onChange={(event) => set({ maxPerRecipe: event.target.value })} className={inputClass} />
              </label>
              <fieldset className="flex flex-col gap-1.5">
                <legend className="mb-1 font-semibold">Ordering</legend>
                <label className="flex items-center gap-2"><input type="checkbox" disabled={!canConfigure} checked={form.avoidAdjacentMarkets} onChange={(event) => set({ avoidAdjacentMarkets: event.target.checked })} />Avoid similar markets back-to-back</label>
                <label className="flex items-center gap-2"><input type="checkbox" disabled={!canConfigure} checked={form.keepPins} onChange={(event) => set({ keepPins: event.target.checked })} />Keep pinned positions</label>
              </fieldset>
            </>
          )}
          <p className="text-[var(--prepacks-muted)]"><span className="font-semibold text-[var(--prepacks-text)]">When packs expire:</span> automatically replaced with an eligible pack.</p>
          {changedElsewhere ? (
            <div role="alert" className="flex flex-col gap-2 rounded border border-[var(--wf-color-warning)] px-3 py-2 text-[var(--wf-color-warning)]">
              This surface’s mix changed elsewhere since you opened it.
              <button type="button" onClick={() => { setBase(latest); setForm(latest) }} className="self-start rounded border border-[var(--prepacks-border)] px-2 py-1 text-[var(--prepacks-text)]">Start again from the saved mix</button>
            </div>
          ) : null}
          {error ? <p role="alert" className="font-semibold text-[var(--wf-color-danger)]">{error}</p> : null}
          {!error && warning ? <p className="text-[var(--wf-color-warning)]">{warning}</p> : null}
          {canConfigure ? <button type="button" onClick={() => setForm(mixDefaults())} className="self-start rounded border border-[var(--prepacks-border)] px-3 py-1 text-[var(--prepacks-text)] hover:bg-[var(--prepacks-raised)]">Reset to defaults</button> : null}
        </form>
        <ProjectedLineup projection={projection} loading={!projection || (previewDirty && previewQuery.isFetching)} error={previewDirty && previewQuery.isError ? previewQuery.error : null} displayCount={displayCount} />
      </div>
    </aside>
  )
}

export function ProjectedLineup({ projection, loading, error, displayCount }) {
  const slots = projection?.slots || []
  const count = Math.max(0, Math.min(50, displayCount))
  // All books (#1091): each book has its own positions, so one list per book
  // with packs here; the notes name a book without any.
  const books = (projection?.books || []).filter((book) => book.counts?.total > 0)
  const lists = books.length ? books.map((book) => ({ label: book.label, slots: slots.filter((slot) => slot.book === book.regulation) })) : [{ label: '', slots }]
  return (
    <section aria-label="Projected lineup" aria-busy={loading || undefined} className="min-w-0 rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] p-3 text-xs">
      <p className="mb-2 font-semibold">Projected lineup · {count} slots{books.length ? ' per book' : ''}</p>
      {error ? <p role="alert" className="mb-2 text-[var(--wf-color-danger)]">{error?.response?.data?.error || 'The projection could not be loaded.'}</p> : null}
      {lists.map((list) => {
        const byPosition = new Map(list.slots.map((slot) => [slot.position, slot]))
        return (
          <div key={list.label}>
            {list.label ? <p className="mb-1 mt-2 font-semibold text-[var(--prepacks-muted)]">{list.label}</p> : null}
            <ol className={`flex flex-col gap-1 ${loading ? 'opacity-60' : ''}`}>
              {Array.from({ length: count }, (_, index) => {
                const slot = byPosition.get(index + 1)
                return (
                  <li key={index} className="grid grid-cols-[20px_10px_minmax(0,1fr)_minmax(0,96px)_14px] items-center gap-2 rounded px-2 py-1 odd:bg-[var(--prepacks-surface)]">
                    <span className="tabular-nums text-[var(--prepacks-muted)]">{index + 1}</span>
                    {slot ? (
                      <>
                        <span className="h-2 w-2 rounded-full" style={{ background: sportHue(slot.sport) }} title={sportLabel(slot.sport)} aria-hidden="true" />
                        <span className="truncate"><span className="font-semibold">{sportLabel(slot.sport)}</span> {slot.title}</span>
                        <span className="truncate rounded bg-[var(--prepacks-raised)] px-1.5 py-0.5 text-[10px] text-[var(--prepacks-text)]">{slot.market || '—'}</span>
                        {slot.pinned ? <Pin className="h-3 w-3 text-[var(--prepacks-accent-text)]" aria-label="Pinned" /> : <span />}
                      </>
                    ) : <span className="col-span-4 italic text-[var(--prepacks-muted)]">Unfilled</span>}
                  </li>
                )
              })}
            </ol>
          </div>
        )
      })}
      {projection?.notes?.length ? <ul className="mt-2 flex flex-col gap-1 text-[var(--wf-color-warning)]">{projection.notes.map((note, index) => <li key={index}>{note.message}</li>)}</ul> : null}
      <p className="mt-2 text-[var(--prepacks-muted)]">Preview uses currently available packs.</p>
    </section>
  )
}
